import { Box, Portal } from '@mui/material';
import React, { createContext, useContext, useState } from 'react';

import { Toast } from './Toast';
import { IToastContent } from './types';

interface IToastContext {
  addToast(toast: Omit<IToastContent, 'id'>): void;
  removeToast(id: string): void;
}

interface IToastContextProviderProps {
  autoDismissToasts?: boolean;
  toastAnchorElement?: HTMLElement;
  children: React.ReactNode;
}

interface IToastStackProps {
  toasts: IToastContent[];
  anchorElement: HTMLElement;
  autoDismissToasts?: boolean;
}

const ToastContext = createContext<IToastContext>({
  addToast: () => {},
  removeToast: () => {}
});

const useToast = () => useContext(ToastContext);

const uuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const ToastStack: React.FC<IToastStackProps> = ({ toasts, anchorElement, autoDismissToasts }) => {
  const { removeToast } = useToast();

  // This will dismiss the first toast after 10 seconds,
  // and then the next toast will be dismissed after 10 seconds, and so on.
  React.useEffect(() => {
    setTimeout(() => {
      if (toasts.length > 0 && autoDismissToasts) {
        removeToast(toasts[0].id);
      }
    }, toasts[0]?.timeout || 10_000);
  }, [toasts]);

  return (
    <Portal container={() => anchorElement}>
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          mx: 1,
          mb: 1,
          display: 'flex',
          height: '90vh',
          overflow: 'auto',
          zIndex: 999,
          flexDirection: 'column',
          justifyContent: 'flex-end',
          gap: 0.5
        }}
      >
        {toasts.map((toast) => {
          return (
            <Toast
              key={toast.id}
              toast={toast}
              onClose={() => {
                removeToast(toast.id);
              }}
            />
          );
        })}
      </Box>
    </Portal>
  );
};

export const ToastContextProvider: React.FC<IToastContextProviderProps> = ({
  autoDismissToasts,
  toastAnchorElement,
  children
}) => {
  const [toasts, setToasts] = useState<IToastContent[]>([]);

  const addToast = React.useCallback(
    (content: IToastContent) => {
      setToasts((t) => [...t, { ...content, id: uuid() }].reverse());
    },
    [toasts]
  );

  const removeToast = React.useCallback(
    (id: string) => {
      setToasts((t) => t.filter((toast) => toast.id !== id));
    },
    [toasts]
  );

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      <ToastStack toasts={toasts} anchorElement={toastAnchorElement} autoDismissToasts={autoDismissToasts} />
      {children}
    </ToastContext.Provider>
  );
};

export { ToastContext, useToast };
